import { useEffect } from 'react'

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// NOTE: Transaction notifications are now handled by the TransactionNotifications component
// This store only tracks the notification state and triggers the notification process

import { CHAIN_IDS, ChainName } from '@src/configs/chainInfo'
import { V1_API_URL, V1_API_URL_TESTNET, V2_API_URL } from '@src/configs/env'
import { ResultCode, StepNames } from '@src/enums'
import { analyticsService } from '@src/services/analytics'
import { isFeatureEnabled } from '@src/utils/environment'
import { getBlockExplorerUrlFromChainId } from '@src/utils/getBlockExplorerUrl'
import { createContextLogger } from '@src/utils/logger'
import { useHistoryStore } from '@src/zustand_stores/history.store'
import { useTokensStore } from '@src/zustand_stores/tokens.store'

const wsLogger = createContextLogger('OrderUpdatesStore')

type LiveOrderData = {
    id: string
    taker_address: string
    maker_address: string
    src_chain: string
    dst_chain: string
    src_asset_address: string
    dst_asset_address: string
    bond_asset_address: string
    src_amount: number
    dst_amount: number
    bond_amount: number
    created_at: Date
    expiration: Date
    status: 'queued' | 'filling' | 'filled' | 'completed'
    completed: boolean
    place_tx: string
    match_tx: string | null
    fill_tx: string | null
    eta: number
    order_type?: 'default' | 'cctp'
}

// Maximum reconnection attempts
const MAX_RECONNECT_ATTEMPTS = 10
// Initial delay in milliseconds before attempting to reconnect
const INITIAL_RECONNECT_DELAY = 1000
// Maximum delay in milliseconds between reconnection attempts
const MAX_RECONNECT_DELAY = 30000

// Connection status for each websocket
type ConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'disconnected' | 'error'

// Track modal state for background notification purposes
type ModalState = {
    wasOpen: boolean // Tracks if modal was open at some point
    isCurrentlyOpen: boolean // Tracks if modal is currently open
    notificationShown: boolean // Tracks if notification was shown for this transaction
}

// Structure to track each active order's websocket connection
type OrderConnection = {
    ws: WebSocket | null
    status: ConnectionStatus
    reconnectAttempts: number
    reconnectTimeoutId: number | null
    lastUpdated: number // timestamp
    modalState: ModalState // Track modal state for this order
}

interface OrderUpdatesStore {
    // Store state
    connections: Record<string, OrderConnection>
    pendingOrderIds: Array<{ exchangeId: string; orderId: string }>

    // Actions
    trackOrder: (exchangeId: string, orderId: string) => void
    stopTrackingOrder: (exchangeId: string, orderId: string) => void
    stopTrackingAllOrders: () => void
    connectToOrder: (exchangeId: string, orderId: string) => void
    disconnectFromOrder: (exchangeId: string, orderId: string) => void

    // Modal state tracking for background notifications
    setModalOpen: (exchangeId: string, orderId: string, isOpen: boolean) => void
    showCompletionNotification: (exchangeId: string, orderId: string) => void
}

// Helper function to get the WebSocket URL
const getWebSocketUrl = (orderId: string): string | null => {
    let webSocketUrl: string | undefined
    let useV2Endpoint = false

    // Determine which base URL to use
    if (isFeatureEnabled('USE_TESTNETS_ONLY')) {
        // Always use testnet URL for testnet mode
        webSocketUrl = V1_API_URL_TESTNET
        // Always use legacy v1 endpoint for testnet
        useV2Endpoint = false
    } else if (isFeatureEnabled('V2_MIGRATION') && V2_API_URL) {
        // Use V2 API URL for mainnet when V2_MIGRATION is enabled
        wsLogger.info('Using V2 API URL for WebSocket connection')
        webSocketUrl = V2_API_URL
        useV2Endpoint = true
    } else {
        // Use legacy mainnet URL
        webSocketUrl = V1_API_URL
        useV2Endpoint = false
    }

    // Convert to WSS protocol
    const wssBaseUrl = webSocketUrl?.replace('https', 'wss')

    // Return the appropriate endpoint URL
    if (wssBaseUrl && orderId) {
        if (useV2Endpoint) {
            // V2 endpoint format
            return `${wssBaseUrl}/orders/${orderId}`
        } else {
            // Legacy v1 endpoint format
            return `${wssBaseUrl}/v1/orders/status/${orderId}`
        }
    }

    return null
}

export const useOrderUpdatesStore = create<OrderUpdatesStore>()(
    persist(
        (set, get) => ({
            connections: {},
            pendingOrderIds: [],

            // Modal state tracking for notification support
            setModalOpen: (exchangeId: string, orderId: string, isOpen: boolean) => {
                const store = get()
                const connectionKey = `${exchangeId}:${orderId}`
                const connection = store.connections[connectionKey]

                wsLogger.debug(`Setting modal state for ${connectionKey} to ${isOpen ? 'OPEN' : 'CLOSED'}`, {
                    hasConnection: !!connection,
                    previousModalState: connection?.modalState,
                })

                if (connection) {
                    // Update the modal state
                    set(state => ({
                        connections: {
                            ...state.connections,
                            [connectionKey]: {
                                ...state.connections[connectionKey],
                                modalState: {
                                    ...state.connections[connectionKey].modalState,
                                    isCurrentlyOpen: isOpen,
                                    // If modal is being opened, mark that it was open at some point
                                    wasOpen: isOpen ? true : state.connections[connectionKey].modalState.wasOpen,
                                },
                            },
                        },
                    }))
                }
            },

            // Show notification for transaction completion
            showCompletionNotification: (exchangeId: string, orderId: string) => {
                const store = get()
                const connectionKey = `${exchangeId}:${orderId}`
                const connection = store.connections[connectionKey]

                wsLogger.debug(`Checking if should show notification for ${connectionKey}`, {
                    hasConnection: !!connection,
                    modalWasOpen: connection?.modalState?.wasOpen,
                    modalCurrentlyOpen: connection?.modalState?.isCurrentlyOpen,
                    notificationAlreadyShown: connection?.modalState?.notificationShown,
                })

                // Only show notification if:
                // 1. We have a connection record for this order
                // 2. Modal is currently closed (user can't see the transaction progress)
                // 3. We haven't already shown a notification for this transaction
                // Note: We used to require modalWasOpen, but that was preventing notifications for transactions
                // that completed without the user ever opening the modal
                if (connection && !connection.modalState.isCurrentlyOpen && !connection.modalState.notificationShown) {
                    wsLogger.info(`Will show notification for ${connectionKey}`)

                    try {
                        // Get transaction data from history
                        const historyStore = useHistoryStore.getState()
                        const txData = historyStore.getOrder(exchangeId)

                        // Extract token symbols for notification message
                        const sellSymbol = txData.sellData?.token?.symbol || 'tokens'
                        const buySymbol = txData.buyData?.token?.symbol || 'tokens'

                        wsLogger.info(`Transaction completed: ${sellSymbol} → ${buySymbol}`, {
                            exchangeId,
                            orderId,
                            sellSymbol,
                            buySymbol,
                            txData: {
                                sellChain: txData.sellData?.chainId,
                                buyChain: txData.buyData?.chainId,
                                result: txData.result?.status,
                            },
                        })

                        // Note: Audio playback and toast notifications have been moved to TransactionNotifications component
                        // We just need to mark that we processed this notification
                        wsLogger.debug(`Notification handling delegated to TransactionNotifications component`)

                        // Mark notification as shown
                        set(state => ({
                            connections: {
                                ...state.connections,
                                [connectionKey]: {
                                    ...state.connections[connectionKey],
                                    modalState: {
                                        ...state.connections[connectionKey].modalState,
                                        notificationShown: true,
                                    },
                                },
                            },
                        }))
                    } catch (error) {
                        wsLogger.error('Error showing notification', { error, exchangeId, orderId })
                    }
                }
            },

            trackOrder: (exchangeId: string, orderId: string) => {
                wsLogger.info('Tracking new order', { exchangeId, orderId })

                // Don't add duplicates
                const store = get()
                const exists = store.pendingOrderIds.some(
                    item => item.exchangeId === exchangeId && item.orderId === orderId
                )

                if (!exists) {
                    set(state => ({
                        pendingOrderIds: [...state.pendingOrderIds, { exchangeId, orderId }],
                    }))

                    // Start websocket connection
                    store.connectToOrder(exchangeId, orderId)
                }
            },

            stopTrackingOrder: (exchangeId: string, orderId: string) => {
                const store = get()
                const historyStore = useHistoryStore.getState()
                const txData = historyStore.getOrder(exchangeId)

                // First disconnect WebSocket
                store.disconnectFromOrder(exchangeId, orderId)

                // Don't immediately remove successful transactions from history
                // This allows UI components like SwapCompleted to continue displaying
                // successful transaction data
                if (!txData || txData?.result?.status !== 'Success') {
                    // Only remove from history if it's not a successful transaction
                    historyStore.removeHistory(exchangeId)
                }

                // Then remove from tracking list
                set(state => ({
                    pendingOrderIds: state.pendingOrderIds.filter(
                        item => !(item.exchangeId === exchangeId && item.orderId === orderId)
                    ),
                }))
            },

            stopTrackingAllOrders: () => {
                const store = get()
                const pendingOrders = [...store.pendingOrderIds]

                // Disconnect all websockets
                pendingOrders.forEach(({ exchangeId, orderId }) => {
                    store.disconnectFromOrder(exchangeId, orderId)
                })

                // Clear the list
                set({ pendingOrderIds: [] })
            },

            connectToOrder: (exchangeId: string, orderId: string) => {
                const store = get()
                const connectionKey = `${exchangeId}:${orderId}`
                const wssUrlEndpoint = getWebSocketUrl(orderId)

                wsLogger.info(`Connecting WebSocket for ${connectionKey}`, {
                    endpoint: wssUrlEndpoint,
                })

                if (!wssUrlEndpoint) {
                    wsLogger.error('Cannot connect to WebSocket, URL is null', { exchangeId, orderId })
                    return
                }

                // Get the current connection or create a new entry
                const connection = store.connections[connectionKey] || {
                    ws: null,
                    status: 'disconnected',
                    reconnectAttempts: 0,
                    reconnectTimeoutId: null,
                    lastUpdated: Date.now(),
                    modalState: {
                        wasOpen: false,
                        isCurrentlyOpen: false,
                        notificationShown: false,
                    },
                }

                // Close existing connection if there is one
                if (connection.ws) {
                    try {
                        connection.ws.onclose = null // Remove onclose handler to prevent reconnection in closeHandler
                        connection.ws.close()
                    } catch (error) {
                        wsLogger.error('Error closing existing websocket:', { error, exchangeId, orderId })
                    }
                    connection.ws = null
                }

                // Clear any existing reconnection timeout
                if (connection.reconnectTimeoutId !== null) {
                    window.clearTimeout(connection.reconnectTimeoutId)
                    connection.reconnectTimeoutId = null
                }

                // Create a new WebSocket connection
                try {
                    wsLogger.info('Connecting to websocket:', { wssUrlEndpoint, exchangeId, orderId })

                    const ws = new WebSocket(wssUrlEndpoint)
                    connection.ws = ws
                    connection.status = 'connecting'

                    // Update the connection in state
                    set(state => ({
                        connections: {
                            ...state.connections,
                            [connectionKey]: { ...connection },
                        },
                    }))

                    ws.onopen = () => {
                        wsLogger.info('WebSocket connection established', { exchangeId, orderId })

                        // Update connection status
                        set(state => ({
                            connections: {
                                ...state.connections,
                                [connectionKey]: {
                                    ...state.connections[connectionKey],
                                    status: 'connected',
                                    reconnectAttempts: 0,
                                    lastUpdated: Date.now(),
                                },
                            },
                        }))
                    }

                    ws.onmessage = event => {
                        try {
                            wsLogger.debug(`WebSocket message for ${connectionKey}`, { type: 'received' })
                            const data: LiveOrderData = JSON.parse(event.data as string)

                            wsLogger.debug(`WebSocket data for ${connectionKey}`, {
                                status: data.status,
                                completed: data.completed,
                                srcChain: data.src_chain,
                                dstChain: data.dst_chain,
                                exchangeId,
                                orderId,
                            })

                            let sourceExplorerUrl = ''
                            let destinationExplorerUrl = ''

                            try {
                                sourceExplorerUrl = getBlockExplorerUrlFromChainId(
                                    CHAIN_IDS[data.src_chain as ChainName]
                                )
                            } catch (e) {
                                wsLogger.error(`Cannot find explorer URL for source chain ${data.src_chain}`)
                            }

                            try {
                                destinationExplorerUrl = getBlockExplorerUrlFromChainId(
                                    CHAIN_IDS[data.dst_chain as ChainName]
                                )
                            } catch (e) {
                                wsLogger.error(`Cannot find explorer URL for destination chain ${data.dst_chain}`)
                            }

                            const sourceTxHash = `${sourceExplorerUrl}/${data.place_tx}`

                            const destinationTxHash = data.fill_tx
                                ? `${destinationExplorerUrl}/${data.fill_tx}`
                                : undefined

                            // Create detailed update data including transaction amounts
                            const updateData = {
                                exchangeId,
                                currentStep: StepNames.MATCH_EXECUTED,
                                result: {
                                    sourceTxHash,
                                    destinationTxHash,
                                    status: data.completed ? ResultCode.success : ResultCode.pending,
                                    message: data.completed ? ResultCode.success : ResultCode.pending,
                                },
                                // We need to get current transaction data to preserve full IExchangeData structure
                                // and only update the values that come from the WebSocket
                            }

                            // Update the history store with new transaction data
                            // This is critical for the UI to show the correct status
                            const historyStore = useHistoryStore.getState()
                            // updateHistory requires 2 arguments: (exchangeId, data)
                            historyStore.updateHistory(exchangeId, {
                                currentStep: updateData.currentStep,
                                result: updateData.result,
                            })

                            if (data.completed) {
                                const store = get()

                                // Track successful trade completion with the backend event
                                // Use analyticsService instead of direct posthog call to ensure consistent tracking
                                // Get token prices from the tokens store if available
                                const tokensStore = useTokensStore.getState()

                                // Try to find tokens by address to get price information
                                const srcToken = tokensStore.tokens.find(
                                    token =>
                                        token.address.toLowerCase() === data.src_asset_address.toLowerCase() &&
                                        token.chainId === CHAIN_IDS[data.src_chain as ChainName]
                                )

                                const dstToken = tokensStore.tokens.find(
                                    token =>
                                        token.address.toLowerCase() === data.dst_asset_address.toLowerCase() &&
                                        token.chainId === CHAIN_IDS[data.dst_chain as ChainName]
                                )

                                // Calculate USD equivalents if price data is available
                                const srcAmountUSD =
                                    srcToken?.price_current && typeof data.src_amount === 'number'
                                        ? (data.src_amount * srcToken.price_current) / 10 ** (srcToken?.decimals || 18)
                                        : undefined

                                const dstAmountUSD =
                                    dstToken?.price_current && typeof data.dst_amount === 'number'
                                        ? (data.dst_amount * dstToken.price_current) / 10 ** (dstToken?.decimals || 18)
                                        : undefined

                                analyticsService.trackEvent('trade_completed_be', {
                                    id: data.id,
                                    taker_address: data.taker_address,
                                    maker_address: data.maker_address,
                                    src_chain: data.src_chain,
                                    dst_chain: data.dst_chain,
                                    src_asset_address: data.src_asset_address,
                                    dst_asset_address: data.dst_asset_address,
                                    src_amount: data.src_amount,
                                    dst_amount: data.dst_amount,
                                    // Add USD equivalents and token prices
                                    src_amount_usd: srcAmountUSD,
                                    dst_amount_usd: dstAmountUSD,
                                    src_token_price: srcToken?.price_current,
                                    dst_token_price: dstToken?.price_current,
                                    src_token_symbol: srcToken?.symbol,
                                    dst_token_symbol: dstToken?.symbol,
                                    place_tx: data.place_tx,
                                    fill_tx: data.fill_tx,
                                    completed: data.completed,
                                    timestamp: new Date().toISOString(),
                                    is_cctp: data.order_type === 'cctp',
                                    swap_type: data.order_type === 'cctp' ? 'cctp' : 'market_maker',
                                    order_type: data.order_type || 'default',
                                })

                                // Update wallet value after transaction completes
                                if (data.taker_address) {
                                    // Determine chain type based on source chain
                                    let chainType = 'EVM'
                                    if (data.src_chain === 'solana') {
                                        chainType = 'SVM'
                                    } else if (data.src_chain === 'tron') {
                                        chainType = 'TRX'
                                    }

                                    // Use source amount USD for contribution tracking
                                    const transactionValueUSD = srcAmountUSD

                                    // Update wallet value and contribution in PostHog
                                    analyticsService.updateWalletValueAfterTransaction(
                                        data.taker_address,
                                        chainType,
                                        transactionValueUSD
                                    )
                                }

                                // Show notification if appropriate
                                store.showCompletionNotification(exchangeId, orderId)

                                // Stop tracking this order
                                store.stopTrackingOrder(exchangeId, orderId)
                            }

                            // Update last updated timestamp
                            set(state => ({
                                connections: {
                                    ...state.connections,
                                    [connectionKey]: {
                                        ...state.connections[connectionKey],
                                        lastUpdated: Date.now(),
                                    },
                                },
                            }))
                        } catch (error) {
                            wsLogger.error('Error processing WebSocket message:', { error, exchangeId, orderId })
                        }
                    }

                    ws.onerror = error => {
                        wsLogger.error('WebSocket error:', { error, exchangeId, orderId })

                        // Update connection status
                        set(state => ({
                            connections: {
                                ...state.connections,
                                [connectionKey]: {
                                    ...state.connections[connectionKey],
                                    status: 'error',
                                    lastUpdated: Date.now(),
                                },
                            },
                        }))
                    }

                    ws.onclose = event => {
                        wsLogger.info('WebSocket connection closed', {
                            code: event.code,
                            reason: event.reason,
                            exchangeId,
                            orderId,
                        })

                        // Handle reconnection
                        const store = get()
                        const currentConnection = store.connections[connectionKey]

                        if (currentConnection) {
                            // Check if we should try to reconnect
                            const historyStore = useHistoryStore.getState()
                            const transactionData = historyStore.getOrder(exchangeId)
                            const isOrderComplete = transactionData.result?.status === ResultCode.success
                            const isOrderTimedOut = transactionData.currentStep === StepNames.MATCH_TIMED_OUT

                            if (
                                !isOrderComplete &&
                                !isOrderTimedOut &&
                                currentConnection.reconnectAttempts < MAX_RECONNECT_ATTEMPTS
                            ) {
                                // Calculate delay with exponential backoff
                                const delay = Math.min(
                                    INITIAL_RECONNECT_DELAY * Math.pow(2, currentConnection.reconnectAttempts),
                                    MAX_RECONNECT_DELAY
                                )

                                wsLogger.info('Scheduling reconnection attempt', {
                                    attempt: currentConnection.reconnectAttempts + 1,
                                    delay,
                                    exchangeId,
                                    orderId,
                                })

                                // Update reconnect attempts
                                set(state => ({
                                    connections: {
                                        ...state.connections,
                                        [connectionKey]: {
                                            ...state.connections[connectionKey],
                                            status: 'reconnecting',
                                            reconnectAttempts: state.connections[connectionKey].reconnectAttempts + 1,
                                            lastUpdated: Date.now(),
                                        },
                                    },
                                }))

                                // Schedule reconnection
                                const timeoutId = window.setTimeout(() => {
                                    store.connectToOrder(exchangeId, orderId)
                                }, delay)

                                // Store the timeout ID
                                set(state => ({
                                    connections: {
                                        ...state.connections,
                                        [connectionKey]: {
                                            ...state.connections[connectionKey],
                                            reconnectTimeoutId: timeoutId,
                                        },
                                    },
                                }))
                            } else if (isOrderComplete || isOrderTimedOut) {
                                // Order is complete or timed out, no need to reconnect
                                wsLogger.info('No reconnection needed - order is completed or timed out', {
                                    exchangeId,
                                    orderId,
                                })

                                // Stop tracking this order
                                store.stopTrackingOrder(exchangeId, orderId)
                            } else {
                                // Max reconnection attempts reached
                                wsLogger.warn('Maximum reconnection attempts reached', { exchangeId, orderId })

                                set(state => ({
                                    connections: {
                                        ...state.connections,
                                        [connectionKey]: {
                                            ...state.connections[connectionKey],
                                            status: 'disconnected',
                                            lastUpdated: Date.now(),
                                        },
                                    },
                                }))
                            }
                        }
                    }
                } catch (error) {
                    wsLogger.error('Error creating WebSocket:', { error, exchangeId, orderId })

                    // Update connection status
                    set(state => ({
                        connections: {
                            ...state.connections,
                            [connectionKey]: {
                                ...state.connections[connectionKey],
                                status: 'error',
                                lastUpdated: Date.now(),
                            },
                        },
                    }))

                    // Attempt to reconnect
                    const store = get()
                    const currentConnection = store.connections[connectionKey]

                    if (currentConnection && currentConnection.reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
                        // Calculate delay with exponential backoff
                        const delay = Math.min(
                            INITIAL_RECONNECT_DELAY * Math.pow(2, currentConnection.reconnectAttempts),
                            MAX_RECONNECT_DELAY
                        )

                        // Schedule reconnection
                        const timeoutId = window.setTimeout(() => {
                            store.connectToOrder(exchangeId, orderId)
                        }, delay)

                        // Store the timeout ID and increment attempts
                        set(state => ({
                            connections: {
                                ...state.connections,
                                [connectionKey]: {
                                    ...state.connections[connectionKey],
                                    reconnectAttempts: state.connections[connectionKey].reconnectAttempts + 1,
                                    reconnectTimeoutId: timeoutId,
                                    status: 'reconnecting',
                                    lastUpdated: Date.now(),
                                },
                            },
                        }))
                    }
                }
            },

            disconnectFromOrder: (exchangeId: string, orderId: string) => {
                const connectionKey = `${exchangeId}:${orderId}`
                const store = get()
                const connection = store.connections[connectionKey]

                if (connection) {
                    // Clear any reconnection timeout
                    if (connection.reconnectTimeoutId !== null) {
                        window.clearTimeout(connection.reconnectTimeoutId)
                    }

                    // Close the WebSocket connection
                    if (connection.ws) {
                        try {
                            connection.ws.onclose = null // Remove onclose handler to prevent reconnection
                            connection.ws.close()
                        } catch (error) {
                            wsLogger.error('Error closing WebSocket during cleanup:', {
                                error,
                                exchangeId,
                                orderId,
                            })
                        }
                    }

                    // Remove the connection from state
                    set(state => {
                        // Use object destructuring to remove the connection
                        const newConnections = { ...state.connections }
                        delete newConnections[connectionKey]
                        return { connections: newConnections }
                    })
                }
            },
        }),
        {
            name: 'order-updates-store',
            // Only persist the pendingOrderIds, not the actual WebSocket connections
            partialize: state => ({
                pendingOrderIds: state.pendingOrderIds,
            }),
        }
    )
)

// Initialize WebSocket connections for all pending orders when the store is hydrated
window.addEventListener('storage', () => {
    setTimeout(() => {
        const store = useOrderUpdatesStore.getState()
        const { pendingOrderIds } = store

        // Connect to all pending orders
        pendingOrderIds.forEach(({ exchangeId, orderId }) => {
            store.connectToOrder(exchangeId, orderId)
        })
    }, 1000) // Small delay to ensure store is fully hydrated
})

// Initialize connections on page load
if (typeof window !== 'undefined') {
    setTimeout(() => {
        const store = useOrderUpdatesStore.getState()
        const { pendingOrderIds } = store

        pendingOrderIds.forEach(({ exchangeId, orderId }) => {
            store.connectToOrder(exchangeId, orderId)
        })
    }, 1000) // Small delay to ensure store is fully hydrated
}

// Logger for orderUpdates background processing
const bgLogger = createContextLogger('[bg-tx]')

// Custom hook to use in components
export const useOrderUpdatesBackground = () => {
    const { trackOrder, stopTrackingOrder, pendingOrderIds, connections, disconnectFromOrder } = useOrderUpdatesStore()
    const getOrder = useHistoryStore.getState().getOrder

    // Clean up default transactions when the component using this hook mounts
    useEffect(() => {
        // Find transactions that should be stopped
        const toRemove = pendingOrderIds.filter(({ exchangeId, orderId }) => {
            const orderData = getOrder(exchangeId)

            // Default transactions (0 -> 0)
            if (orderData.sellData?.value === BigInt(0) && orderData.buyData?.value === BigInt(0)) {
                bgLogger.info(`Stopping tracking of default transaction with 0->0 values`, { exchangeId, orderId })
                return true
            }

            // Completed or timed out transactions
            if (
                orderData.currentStep === StepNames.MATCH_TIMED_OUT ||
                orderData.result?.status === ResultCode.success
            ) {
                bgLogger.info(`Stopping tracking of completed/timed out transaction`, {
                    exchangeId,
                    orderId,
                    currentStep: orderData.currentStep,
                    resultStatus: orderData.result?.status,
                })
                return true
            }

            // Transactions older than 20 minutes
            if (orderData.createdAt) {
                // Safely convert createdAt to Date object
                const creationTime = new Date(orderData.createdAt as string | number | Date).getTime()
                const currentTime = Date.now()
                const ageInMinutes = (currentTime - creationTime) / (1000 * 60)

                if (ageInMinutes > 20) {
                    bgLogger.info(`Stopping tracking of transaction older than 20 minutes`, {
                        exchangeId,
                        orderId,
                        ageInMinutes: Math.floor(ageInMinutes),
                    })
                    return true
                }
            }

            return false
        })

        if (toRemove.length > 0) {
            bgLogger.info(`Stopping tracking for ${toRemove.length} unwanted transactions`)

            // Stop tracking these transactions
            toRemove.forEach(({ exchangeId, orderId }) => {
                try {
                    stopTrackingOrder(exchangeId, orderId)
                    disconnectFromOrder(exchangeId, orderId)
                } catch (error) {
                    bgLogger.error(`Error stopping tracking for order:`, { exchangeId, orderId, error })
                }
            })
        }
    }, [pendingOrderIds])

    return {
        trackOrder,
        stopTrackingOrder,
        pendingOrderIds,
        // Return connection statuses for UI purposes
        connectionStatuses: Object.entries(connections).reduce(
            (acc, [key, conn]) => {
                const [exchangeId, orderId] = key.split(':')
                acc[key] = {
                    exchangeId,
                    orderId,
                    status: conn.status,
                    lastUpdated: conn.lastUpdated,
                }
                return acc
            },
            {} as Record<string, { exchangeId: string; orderId: string; status: ConnectionStatus; lastUpdated: number }>
        ),
    }
}
